import React, { useState, useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useInstanceStore } from '../../stores/UseInstanceStore';

// Polling helper function - polls until done or timeout
async function pollUntilDone(portfolioId, ticketId, signal, onUpdate, apiBaseUrl, authTokenRef) {
  const maxAttempts = 150; // ~10 minutes at ~4s average interval
  let attempts = 0;

  while (attempts < maxAttempts) {
    if (signal.aborted) return;

    try {
      const token = authTokenRef.current;
      const headers = { 'Accept': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(
        `${apiBaseUrl}/portfolio/${portfolioId}/research/poll/${ticketId}`,
        { signal, headers }
      );

      if (response.status === 401) {
        onUpdate({ done: true, status: 'FAILED', error: 'Session expired. Please sign in again.' });
        return;
      }

      if (!response.ok) {
        console.warn(`Poll attempt ${attempts + 1} returned ${response.status}, retrying...`);
        await new Promise((resolve) => setTimeout(resolve, 3000));
        attempts++;
        continue;
      }

      const poll = await response.json();
      onUpdate(poll);

      if (poll.done) return;

      const delay = poll.status === 'PENDING' ? 3000 : 5000;
      await new Promise((resolve) => setTimeout(resolve, delay));
      attempts++;
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn(`Poll attempt ${attempts + 1} failed: ${err.message}, retrying...`);
      await new Promise((resolve) => setTimeout(resolve, 3000));
      attempts++;
    }
  }

  onUpdate({
    done: true,
    status: 'TIMEOUT',
    error: 'Research run timed out after 10 minutes',
  });
}

export default function ResearchRunStatus({
  portfolioId,
  apiBaseUrl: apiBaseUrlProp,
  authToken,
  ticketId,
  onCompleted,
  onDismiss,
}) {
  const apiBaseUrl = apiBaseUrlProp || window.location.origin.replace('/web', '');
  const [pollStatus, setPollStatus] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const authTokenRef = useRef(authToken);

  // Keep ref current so the polling loop always uses the latest token
  useEffect(() => {
    authTokenRef.current = authToken;
  }, [authToken]);

  const { setResearchTicketId, setResearchPollDone } = useInstanceStore();

  useEffect(() => {
    if (!ticketId) return;

    setResearchTicketId(ticketId);
    setResearchPollDone(false);

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    console.log(`ResearchRunStatus: starting poll for ticket ${ticketId}`);
    setError(null);

    pollUntilDone(portfolioId, ticketId, signal, (poll) => {
      setPollStatus(poll.status);

      if (poll.done) {
        setResearchPollDone(true);
        setResearchTicketId(null);

        if (poll.status === 'FAILED' || poll.status === 'TIMEOUT') {
          setError(poll.error || 'Research run failed');
        } else if (poll.status === 'COMPLETED' && poll.response) {
          onCompleted(poll.response);
        }
      }
    }, apiBaseUrl, authTokenRef);

    return () => {
      abortRef.current?.abort();
    };
  }, [ticketId, portfolioId]);

  const handleDismiss = () => {
    abortRef.current?.abort();
    setResearchTicketId(null);
    setResearchPollDone(false);
    onDismiss();
  };

  const statusConfig = {
    PENDING: {
      icon: (
        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ),
      color: 'text-slate-400',
      bgColor: 'bg-slate-900/30',
      borderColor: 'border-slate-700',
      message: 'Research queued...',
      subMessage: 'Your research run is waiting to start.',
    },
    RUNNING: {
      icon: (
        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ),
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-900/30',
      borderColor: 'border-cyan-800',
      message: 'Analyzing market data...',
      subMessage: 'The research agent is processing your request. This can take a while. (up to 10m)',
    },
    COMPLETED: {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      color: 'text-green-400',
      bgColor: 'bg-green-900/30',
      borderColor: 'border-green-800',
      message: 'Complete',
      subMessage: 'Scroll down to see your research results.',
    },
    FAILED: {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-red-400',
      bgColor: 'bg-red-900/30',
      borderColor: 'border-red-800',
      message: 'Failed',
      subMessage: error || 'Research run failed to complete.',
    },
    TIMEOUT: {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-amber-400',
      bgColor: 'bg-amber-900/30',
      borderColor: 'border-amber-800',
      message: 'Timed out',
      subMessage: 'Run timed out. You can check results later.',
    },
  };

  const cfg = pollStatus ? statusConfig[pollStatus] : null;

  if (!pollStatus) return null;

  return (
    <div className={`mt-4 p-4 border rounded-lg ${cfg.bgColor} ${cfg.borderColor}`}>
      <div className="flex items-start gap-3">
        <div className={cfg.color}>{cfg.icon}</div>
        <div className="flex-1 min-w-0">
          <p className={`${cfg.color} font-medium`}>{cfg.message}</p>
          <p className={`text-sm ${pollStatus === 'FAILED' || pollStatus === 'TIMEOUT' ? cfg.color : 'text-slate-400'}`}>
            {cfg.subMessage}
          </p>
        </div>
        {(pollStatus === 'FAILED' || pollStatus === 'TIMEOUT') && (
          <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-300 p-1">
            <FaTimes className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
